import type { LucideIcon } from "lucide-react"
import {
  BarChart3,
  Bot,
  GaugeCircle,
  Megaphone,
  PanelsTopLeft,
  PlugZap,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Truck,
} from "lucide-react"

export type FeatureSpotlight = {
  icon: LucideIcon
  title: string
  description: string
  metrics: string[]
}

export type FeatureCard = {
  icon: LucideIcon
  title: string
  description: string
}

export type EnablementHighlight = {
  eyebrow: string
  headline: string
  copy: string
}

export type Stat = {
  label: string
  value: string
  detail: string
}

export const hero = {
  eyebrow: "AI feature stack",
  title: "AI features that make Braise the next-gen POS revenue partner",
  subtitle:
    "This POS is no bluff - it's a launch-ready AI operating system that optimizes menu layouts, recommends upsells, predicts popular items, and adapts to crews in multiple languages. Our first objective is simple: help you increase revenue while keeping navigation effortless for every teammate.",
  productCard: {
    label: "Your product (today’s launch)",
    title: "- What the POS actually does best right now",
    copy:
      "Rush-mode UI, AI guest flows, and forecasting copilots give restaurants and food trucks a personalized, revenue-generating experience from day one. Anyone on the team can run Braise single-handedly and still feel like they have an AI strategist beside them.",
  },
  ctas: {
    primaryTo: "/pricing",
    primaryLabel: "See pricing",
    secondaryTo: "/stories",
    secondaryLabel: "Watch operator stories",
  },
} as const

export const stats: Stat[] = [
  {
    label: "Launch horizon",
    value: "Today → go-live",
    detail: "Ops teams ship the AI stack in under two weeks.",
  },
  {
    label: "Languages live",
    value: "12+",
    detail: "Multi-language prompts for crews and guests out of the box.",
  },
  {
    label: "Signals analyzed daily",
    value: "28M+",
    detail: "Menu, demand, and inventory signals fuel every suggestion.",
  },
  {
    label: "Experiments running",
    value: "400+",
    detail: "Menu layout, imagery, and copy tests stay on autopilot.",
  },
]

export const featureSpotlights: FeatureSpotlight[] = [
  {
    icon: GaugeCircle,
    title: "Rush-Mode UI",
    description:
      "Ultra-fast order entry, two-tap modifiers, quick 86’ing, and auto-prioritized tickets keep crews calm in peak rush.",
    metrics: ["Two-tap modifiers", "Auto-prioritized tickets"],
  },
  {
    icon: Sparkles,
    title: "AI Guest Assistant (QR)",
    description:
      "Personalized, context-aware ordering flows greet every guest in their language with suggested pairings and dietary flags.",
    metrics: ["Dynamic menu sequencing", "Multi-language prompts"],
  },
  {
    icon: TrendingUp,
    title: "Tip Intelligence",
    description:
      "Data-driven prompts factor order size, party mix, and service style so teams raise take-home pay without guest friction.",
    metrics: ["Party-aware prompts", "Fair take-home lift"],
  },
  {
    icon: Megaphone,
    title: "Ad / Promo Intelligence",
    description:
      "Identifies items with headroom and spins up BOGO, bundles, or time-boxed offers that protect contribution margin.",
    metrics: ["Margin-safe bundles", "Automated promo guardrails"],
  },
]

export const featureCatalog: FeatureCard[] = [
  {
    icon: PanelsTopLeft,
    title: "Menu Intelligence & A/B",
    description:
      "Learns what sells per venue, daypart, or event and continuously tests imagery and copy to raise attach rate.",
  },
  {
    icon: BarChart3,
    title: "Demand Forecasting",
    description:
      "Predicts popular items by hour, day, or event, pre-flushing prep plans and signaling when to fire new batches.",
  },
  {
    icon: ShieldCheck,
    title: "86 Sync & Service Guard",
    description:
      "Inventory-aware guardrails hide items everywhere (QR, counter, KDS) and propose smart substitutions instantly.",
  },
  {
    icon: Bot,
    title: "Operator Copilot & Reporting",
    description:
      "Natural-language queries like “Show taco sales for last four Saturdays” produce anomaly alerts and plain-English summaries.",
  },
  {
    icon: Truck,
    title: "Food-Truck Mode",
    description:
      "Offline-tolerant flows, low-tap menus, and line-busting QR make every route or festival feel dialed in.",
  },
  {
    icon: PlugZap,
    title: "Open hooks",
    description:
      "Exports, webhooks, and APIs for menus, orders, and receipts keep enterprise pilots moving without rewrites.",
  },
]

export const enablementHighlights: EnablementHighlight[] = [
  {
    eyebrow: "Mission-first partnership",
    headline: "Braise is the revenue-generating teammate, not just another POS",
    copy:
      "We focus on being the most efficient partner restaurants and food trucks can trust - raising revenue, simplifying workflows, and giving crews multi-language flows they can run solo.",
  },
  {
    eyebrow: "AI in plain sight",
    headline: "System AI optimizes menus, upsells, and prep before the rush hits",
    copy:
      "Menu layouts reorganize themselves, upsells surface at the perfect moment, and predictive demand cues prep teams before back-of-house ever feels behind.",
  },
  {
    eyebrow: "Market revolution",
    headline: "We are here to rewrite what restaurant POS should deliver",
    copy:
      "With rush-mode UI, predictive copilots, and open hooks, Braise is revolutionizing how restaurants and food trucks run every channel from a single AI-native operating system.",
  },
]

export const useCases = {
  tabs: [
    {
      id: "crew",
      label: "Crew",
      blurb:
        "Keep lines moving with low-tap flows, prioritized tickets, and smart 86’ing that keeps everyone in sync.",
      items: [
        featureSpotlights[0],
        featureCatalog[2],
        featureSpotlights[2],
      ],
    },
    {
      id: "guest",
      label: "Guest",
      blurb:
        "Personalized ordering that respects dietary needs, language, and context - with upsells that feel helpful.",
      items: [
        featureSpotlights[1],
        featureCatalog[0],
        featureCatalog[4],
      ],
    },
    {
      id: "owner",
      label: "Owner",
      blurb:
        "See what’s working in plain English, forecast demand, and connect data to the rest of your stack.",
      items: [
        featureCatalog[1],
        featureCatalog[3],
        featureCatalog[5],
      ],
    },
  ],
} as const

